<script setup>
/**
 * pages/loyalty/index.vue — Addendum 07, Modul U
 * ─────────────────────────────────────────────────────────
 * 2 tab: (1) Pengaturan program (mode/rate/stempel) — pembatasan mode PER
 * PAKET ditegakkan backend (LoyaltyService::assertModeAllowed()), halaman
 * ini cuma menampilkan pesan error-nya kalau ditolak, TIDAK menduplikasi
 * logic pembatasannya di frontend. (2) Cari customer -> lihat/koreksi saldo.
 * Pemakaian poin sesungguhnya saat checkout (Modul V) belum dikerjakan di
 * modul ini.
 */
import { useApi } from '@/utils/api'
import { useApiRequest } from '@/composables/useApiRequest'
import { useAbility } from '@casl/vue'
import { formatRupiah, formatDateTime } from '@/composables/useFormat'
import { requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Loyalty',
  },
})

const ability = useAbility()
const { errors, request } = useApiRequest()

const activeTab = ref('settings')

// -----------------------------------------------------------------------
// Tab 1: Pengaturan
// -----------------------------------------------------------------------
const modeOptions = [
  { title: 'Nonaktif', value: 'none' },
  { title: 'Poin', value: 'points' },
  { title: 'Stempel', value: 'stamps' },
  { title: 'Poin & Stempel (Keduanya)', value: 'both' },
]

const stampRewardOptions = [
  { title: 'Tidak ada', value: null },
  { title: 'Produk Gratis', value: 'free_product' },
  { title: 'Diskon', value: 'discount' },
]

const settingsForm = ref({
  mode: 'none',
  points_earn_rate: 0,
  points_redeem_rate: 0,
  min_redeem_points: null,
  stamp_required_count: null,
  stamp_reward_type: null,
  stamp_reward_product_id: null,
  stamp_reward_discount_amount: null,
})

const settingsFormRef = ref()
const settingsLoading = ref(false)
const settingsSaving = ref(false)
const products = ref([])

const fetchSettings = async () => {
  settingsLoading.value = true
  try {
    const response = await useApi('/api/v1/loyalty/settings').get()
    const data = response?.data ?? response

    settingsForm.value = {
      mode: data.mode,
      points_earn_rate: data.points_earn_rate,
      points_redeem_rate: data.points_redeem_rate,
      min_redeem_points: data.min_redeem_points,
      stamp_required_count: data.stamp_required_count,
      stamp_reward_type: data.stamp_reward_type,
      stamp_reward_product_id: data.stamp_reward_product_id,
      stamp_reward_discount_amount: data.stamp_reward_discount_amount,
    }
  } finally {
    settingsLoading.value = false
  }
}

const fetchProducts = async () => {
  const response = await useApi('/api/v1/products', { query: { per_page: 200, is_active: 1 } }).get()
  const data = response?.data ?? response

  products.value = data?.data ?? []
}

const saveSettings = async () => {
  const { valid } = await settingsFormRef.value?.validate()
  if (!valid) return

  settingsSaving.value = true

  // Kalau pindah dari stamp_reward_type -> null, pastikan field terkait ikut
  // null (bukan sisa nilai lama) — sama pola dengan promo/voucher.
  const payload = { ...settingsForm.value }
  if (payload.stamp_reward_type !== 'free_product') payload.stamp_reward_product_id = null
  if (payload.stamp_reward_type !== 'discount') payload.stamp_reward_discount_amount = null

  const res = await request(() => useApi('/api/v1/loyalty/settings').put({ body: payload }))

  settingsSaving.value = false
  if (res) await fetchSettings()
}

// -----------------------------------------------------------------------
// Tab 2: Cari Customer -> Saldo & Koreksi
// -----------------------------------------------------------------------
const phoneQuery = ref('')
const searchResults = ref([])
const searching = ref(false)
const hasSearched = ref(false)

const selectedCustomer = ref(null)
const transactions = ref([])
const loadingBalance = ref(false)

const adjustDialog = ref(false)
const adjustForm = ref({ amount: null, notes: '' })
const adjustFormRef = ref()
const adjustLoading = ref(false)

const typeLabels = {
  earn: 'Perolehan',
  redeem: 'Penukaran',
  adjustment: 'Koreksi Manual',
  expire: 'Kedaluwarsa',
}

const searchCustomer = async () => {
  const phone = phoneQuery.value.trim()

  if (phone.length < 3) {
    searchResults.value = []
    hasSearched.value = false

    return
  }

  searching.value = true
  try {
    const response = await useApi('/api/v1/customers/search', { query: { phone } }).get()

    searchResults.value = response?.data ?? response ?? []
  } finally {
    searching.value = false
    hasSearched.value = true
  }
}

watchDebounced(phoneQuery, searchCustomer, { debounce: 400 })

const loadBalance = async customer => {
  selectedCustomer.value = customer
  loadingBalance.value = true
  try {
    const response = await useApi(`/api/v1/loyalty/customers/${customer.id}`).get()
    const data = response?.data ?? response

    selectedCustomer.value = data.customer
    transactions.value = data.transactions ?? []
  } finally {
    loadingBalance.value = false
  }
}

const openAdjustDialog = () => {
  adjustForm.value = { amount: null, notes: '' }
  adjustFormRef.value?.reset()
  adjustDialog.value = true
}

const submitAdjust = async () => {
  const { valid } = await adjustFormRef.value?.validate()
  if (!valid) return

  adjustLoading.value = true

  const res = await request(() =>
    useApi(`/api/v1/loyalty/customers/${selectedCustomer.value.id}/adjust`).post({ body: adjustForm.value }),
  )

  adjustLoading.value = false
  if (res) {
    adjustDialog.value = false
    await loadBalance(selectedCustomer.value)
  }
}

// -----------------------------------------------------------------------
// Lifecycle
// -----------------------------------------------------------------------
onMounted(() => {
  fetchSettings()
  fetchProducts()
})
</script>

<template>
  <div>
    <VRow class="mb-2">
      <VCol cols="12">
        <h4 class="text-h4 font-weight-medium mb-1">Program Loyalty</h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Kelola program poin/stempel dan lihat/koreksi saldo per customer.
        </p>
      </VCol>
    </VRow>

    <VTabs v-model="activeTab" class="mb-4">
      <VTab value="settings">Pengaturan</VTab>
      <VTab value="balances">Saldo Customer</VTab>
    </VTabs>

    <!-- ================================================================
         Tab: Pengaturan
    ================================================================ -->
    <VCard v-show="activeTab === 'settings'">
      <VForm ref="settingsFormRef" @submit.prevent="saveSettings">
        <VCardText>
          <VAlert type="info" variant="tonal" density="compact" class="mb-4">
            Ketersediaan mode loyalty tergantung paket langganan tenant ini —
            Starter tidak bisa mengaktifkan loyalty sama sekali, Pro cuma boleh
            pilih SATU mode (poin atau stempel), Premium boleh keduanya sekaligus.
            Pembatasan ini ditegakkan di server, bukan cuma disembunyikan di sini.
          </VAlert>

          <VRow>
            <VCol cols="12" sm="6">
              <VSelect
                v-model="settingsForm.mode"
                :items="modeOptions"
                label="Mode Loyalty"
                :rules="[requiredValidator]"
                :error-messages="errors.mode"
              />
            </VCol>
          </VRow>

          <template v-if="settingsForm.mode === 'points' || settingsForm.mode === 'both'">
            <VDivider class="my-4" />
            <p class="text-body-1 font-weight-medium mb-2">
              <VIcon icon="ri-coins-line" size="18" class="me-1" />
              Poin
            </p>
            <VRow>
              <VCol cols="12" sm="4">
                <VTextField
                  v-model.number="settingsForm.points_earn_rate"
                  label="Poin per Rupiah Belanja"
                  type="number" min="0" step="any"
                  hint="Mis. 0.01 = Rp10.000 belanja dapat 100 poin"
                  persistent-hint
                  :rules="[requiredValidator]"
                  :error-messages="errors.points_earn_rate"
                />
              </VCol>
              <VCol cols="12" sm="4">
                <VTextField
                  v-model.number="settingsForm.points_redeem_rate"
                  label="Nilai Rupiah per Poin (Tukar)"
                  type="number" min="0" step="any"
                  hint="Mis. 100 = 1 poin senilai Rp100 potongan"
                  persistent-hint
                  :rules="[requiredValidator]"
                  :error-messages="errors.points_redeem_rate"
                />
              </VCol>
              <VCol cols="12" sm="4">
                <VTextField
                  v-model.number="settingsForm.min_redeem_points"
                  label="Minimal Penukaran (opsional)"
                  type="number" min="1" step="1"
                  hint="Kosongkan = tidak ada minimum"
                  persistent-hint
                  :error-messages="errors.min_redeem_points"
                />
              </VCol>
            </VRow>
          </template>

          <template v-if="settingsForm.mode === 'stamps' || settingsForm.mode === 'both'">
            <VDivider class="my-4" />
            <p class="text-body-1 font-weight-medium mb-2">
              <VIcon icon="ri-stack-line" size="18" class="me-1" />
              Stempel
            </p>
            <VRow>
              <VCol cols="12" sm="4">
                <VTextField
                  v-model.number="settingsForm.stamp_required_count"
                  label="Jumlah Stempel untuk Reward"
                  type="number" min="1" step="1"
                  :error-messages="errors.stamp_required_count"
                />
              </VCol>
              <VCol cols="12" sm="4">
                <VSelect
                  v-model="settingsForm.stamp_reward_type"
                  :items="stampRewardOptions"
                  label="Tipe Reward"
                  :error-messages="errors.stamp_reward_type"
                />
              </VCol>
              <VCol v-if="settingsForm.stamp_reward_type === 'free_product'" cols="12" sm="4">
                <VSelect
                  v-model="settingsForm.stamp_reward_product_id"
                  :items="products"
                  item-title="name"
                  item-value="id"
                  label="Produk Hadiah"
                  :error-messages="errors.stamp_reward_product_id"
                />
              </VCol>
              <VCol v-if="settingsForm.stamp_reward_type === 'discount'" cols="12" sm="4">
                <VTextField
                  v-model.number="settingsForm.stamp_reward_discount_amount"
                  label="Nominal Diskon (Rp)"
                  type="number" min="0" step="any"
                  :error-messages="errors.stamp_reward_discount_amount"
                />
              </VCol>
            </VRow>
          </template>
        </VCardText>

        <VDivider />

        <VCardActions class="pa-4">
          <VSpacer />
          <VBtn
            v-if="ability.can('adjust', 'Loyalty')"
            type="submit"
            color="primary"
            :loading="settingsSaving"
            :disabled="settingsLoading"
          >
            Simpan Pengaturan
          </VBtn>
        </VCardActions>
      </VForm>
    </VCard>

    <!-- ================================================================
         Tab: Saldo Customer
    ================================================================ -->
    <div v-show="activeTab === 'balances'">
      <VCard class="mb-4">
        <VCardText>
          <VTextField
            v-model="phoneQuery"
            label="Cari nomor HP customer"
            placeholder="08xxxxxxxxxx"
            prepend-inner-icon="ri-phone-line"
            density="compact"
            variant="outlined"
            clearable
            hide-details
          />

          <div v-if="searching" class="d-flex justify-center pa-4">
            <VProgressCircular indeterminate color="primary" size="24" />
          </div>

          <VList v-else-if="searchResults.length > 0" class="mt-2" density="compact">
            <VListItem
              v-for="customer in searchResults"
              :key="customer.id"
              :title="customer.name"
              :subtitle="customer.phone"
              @click="loadBalance(customer)"
            >
              <template #prepend>
                <VIcon icon="ri-user-line" />
              </template>
            </VListItem>
          </VList>

          <p v-else-if="hasSearched" class="text-body-2 text-medium-emphasis mt-3 mb-0">
            Tidak ada customer dengan nomor tersebut.
          </p>
        </VCardText>
      </VCard>

      <VCard v-if="selectedCustomer">
        <VCardText v-if="loadingBalance" class="d-flex justify-center pa-8">
          <VProgressCircular indeterminate color="primary" />
        </VCardText>

        <template v-else>
          <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3">
            <div>
              <h6 class="text-h6 mb-1">{{ selectedCustomer.name }}</h6>
              <span class="text-body-2 text-medium-emphasis">{{ selectedCustomer.phone }}</span>
            </div>
            <div class="d-flex gap-4">
              <div class="text-center">
                <div class="text-h5 font-weight-bold">{{ selectedCustomer.loyalty_points_balance }}</div>
                <div class="text-caption text-medium-emphasis">Poin</div>
              </div>
              <div class="text-center">
                <div class="text-h5 font-weight-bold">{{ selectedCustomer.stamp_count }}</div>
                <div class="text-caption text-medium-emphasis">Stempel</div>
              </div>
              <div class="text-center">
                <div class="text-h6 font-weight-bold">{{ formatRupiah(selectedCustomer.total_spent) }}</div>
                <div class="text-caption text-medium-emphasis">{{ selectedCustomer.total_orders_count }}x Belanja</div>
              </div>
            </div>
            <VBtn
              v-if="ability.can('adjust', 'Loyalty')"
              variant="tonal"
              color="primary"
              prepend-icon="ri-edit-line"
              @click="openAdjustDialog"
            >
              Koreksi Saldo Poin
            </VBtn>
          </VCardText>

          <VDivider />

          <VTable density="compact">
            <thead>
              <tr>
                <th>Waktu</th>
                <th>Tipe</th>
                <th class="text-end">Poin</th>
                <th class="text-end">Stempel</th>
                <th class="text-end">Saldo Setelah</th>
                <th>Catatan</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="tx in transactions" :key="tx.id">
                <td>{{ formatDateTime(tx.created_at) }}</td>
                <td>
                  <VChip size="x-small" variant="tonal">{{ typeLabels[tx.type] ?? tx.type }}</VChip>
                </td>
                <td class="text-end">{{ tx.points_amount }}</td>
                <td class="text-end">{{ tx.stamps_amount }}</td>
                <td class="text-end">{{ tx.balance_after }}</td>
                <td>{{ tx.notes ?? '-' }}</td>
              </tr>
              <tr v-if="transactions.length === 0">
                <td colspan="6" class="text-center text-medium-emphasis py-4">
                  Belum ada riwayat transaksi loyalty.
                </td>
              </tr>
            </tbody>
          </VTable>
        </template>
      </VCard>
    </div>

    <!-- ================================================================
         Dialog: Koreksi Saldo
    ================================================================ -->
    <VDialog v-model="adjustDialog" max-width="440">
      <VCard>
        <VCardTitle class="pa-4 pb-0">Koreksi Saldo Poin</VCardTitle>
        <VForm ref="adjustFormRef" @submit.prevent="submitAdjust">
          <VCardText class="pt-3">
            <VTextField
              v-model.number="adjustForm.amount"
              label="Jumlah (+ tambah / - kurangi)"
              type="number"
              step="1"
              class="mb-3"
              hint="Mis. 50 untuk menambah 50 poin, -50 untuk mengurangi"
              persistent-hint
              :rules="[requiredValidator]"
              :error-messages="errors.amount"
            />
            <VTextarea
              v-model="adjustForm.notes"
              label="Catatan (wajib diisi)"
              rows="2"
              :rules="[requiredValidator]"
              :error-messages="errors.notes"
            />
          </VCardText>

          <VDivider />

          <VCardActions class="pa-4">
            <VSpacer />
            <VBtn variant="text" @click="adjustDialog = false">Batal</VBtn>
            <VBtn type="submit" color="primary" :loading="adjustLoading">Simpan</VBtn>
          </VCardActions>
        </VForm>
      </VCard>
    </VDialog>
  </div>
</template>
